import {
  OktaAuth,
  type IdxTransaction,
  AuthApiError,
} from "@okta/okta-auth-js";
import { clearOktaSessionWithLogoutEndpoint } from "./oktaHelper";
import {
  extractAuthApiErrorI18nKey,
  findByName,
  getEnrollmentAuthenticator,
  getItemFieldFromCollectionByProp,
  getOtpStateHandle,
  getRemediationValues,
  getStringProp,
  getTransactionStateHandle,
} from "./otpHelper";
import type { IntlShape } from "react-intl";

export type IdxMessage = { message?: string; i18n?: { key?: string } };
export type OtpTransaction = IdxTransaction;

export interface StartOtpFlowResult {
  transaction: OtpTransaction;
  stateHandle: string;
}

export interface VerifyOtpCodeResult {
  transaction: OtpTransaction;
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresIn: number;
}

const tokenExpiresIn = 3600;

export const resolveOtpErrorMessage = (
  intl: IntlShape,
  isSessionExpired?: boolean
) => {
  if (isSessionExpired) {
    return intl.formatMessage({
      id: "oneTimeCodeExpired",
      defaultMessage:
        'This code has expired. Please click "Resend" to receive a new code.',
    });
  }
  return intl.formatMessage({
    id: "oneTimeCodeInvalid",
    defaultMessage: "Wrong verification code. Please try again.",
  });
};

export const createOtpOktaAuth = (oktaConfig: {
  issuer: string;
  clientId: string;
  scopes: string[];
  tokenManager: Record<string, unknown>;
  pkce: boolean;
  redirectUri?: string;
}) => {
  const redirectUri =
    oktaConfig.redirectUri ??
    (typeof window !== "undefined" ? `${window.location.origin}/` : undefined);
  return new OktaAuth({
    issuer: oktaConfig.issuer,
    clientId: oktaConfig.clientId,
    scopes: oktaConfig.scopes,
    tokenManager: oktaConfig.tokenManager,
    pkce: oktaConfig.pkce,
    ...(redirectUri && { redirectUri }),
  });
};

export const getOtpTiming = (resendTimeoutMs?: number) => {
  const safeResendTimeoutMs = resendTimeoutMs ?? 60000;
  const resendTimeoutSeconds = Math.max(
    1,
    Math.ceil(safeResendTimeoutMs / 1000)
  );
  const otpExpiryMinutes = 60;

  return {
    resendTimeoutSeconds,
    otpExpiryMinutes,
  };
};

/**
 * Without an explicit `step`, IDX picks the first remediation that `canRemediate` — often
 * `authenticator-verification-data` for the *current* factor (e.g. phone). That remediator
 * then merges phone `id` / `enrollmentId` with the caller's `methodType: "email"`.
 * Forcing `select-authenticator-authenticate` makes the challenge body use the email authenticator id.
 */
const shouldForceSelectAuthenticatorForEmail = (
  transaction: OtpTransaction | undefined
): boolean => {
  if (!transaction) return false;
  const remediation = getRemediationValues(transaction);
  if (!findByName(remediation, "select-authenticator-authenticate")) {
    return false;
  }
  const currentType = getStringProp(
    getEnrollmentAuthenticator(transaction),
    "type"
  );
  return currentType !== "email";
};

const proceedWithEmailAuthenticator = async (
  oktaAuth: OktaAuth,
  stateHandle: string,
  authenticatorId: string,
  options?: {
    /** Pass the post-`authenticate` transaction so we can force the select-authenticator step when needed. */
    idxTransaction?: OtpTransaction;
  }
): Promise<StartOtpFlowResult> => {
  const forceSelectStep = shouldForceSelectAuthenticatorForEmail(
    options?.idxTransaction
  );

  const nextTransaction = await oktaAuth.idx.proceed({
    stateHandle,
    ...(forceSelectStep && { step: "select-authenticator-authenticate" }),
    authenticator: {
      id: authenticatorId,
      methodType: "email",
    },
  });

  return {
    transaction: nextTransaction,
    stateHandle: getOtpStateHandle(nextTransaction) ?? stateHandle,
  };
};

export const startOtpIdxFlow = async (
  oktaAuth: OktaAuth,
  email: string
): Promise<StartOtpFlowResult> => {
  await clearOktaSessionWithLogoutEndpoint(oktaAuth);

  const transaction = await oktaAuth.idx.authenticate({
    username: email,
  });
  if (!transaction) {
    throw new Error("Unexpected authenticate response");
  }

  const stateHandle = getTransactionStateHandle(transaction);

  const emailAuthenticatorId = getItemFieldFromCollectionByProp(
    transaction.context,
    "authenticators",
    "type",
    "email",
    "id"
  );

  if (!emailAuthenticatorId) {
    throw new Error("Email authenticator not found in transaction");
  }

  return proceedWithEmailAuthenticator(
    oktaAuth,
    stateHandle,
    emailAuthenticatorId,
    { idxTransaction: transaction }
  );
};

export const verifyOtpCode = async (
  oktaAuth: OktaAuth,
  stateHandle: string,
  code: string
): Promise<VerifyOtpCodeResult> => {
  try {
    const tx = await oktaAuth.idx.proceed({
      stateHandle,
      credentials: { passcode: code },
    });

    if (tx.status !== "SUCCESS") {
      throw new Error(JSON.stringify(tx.messages?.[0]));
    }

    let accessToken: string | undefined;
    let idToken: string | undefined;
    let refreshToken: string | undefined;

    if (tx?.tokens?.accessToken?.accessToken) {
      accessToken = tx.tokens.accessToken.accessToken;
      idToken = tx.tokens.idToken?.idToken;
      refreshToken = tx.tokens.refreshToken?.refreshToken;
    } else {
      const interactionCode = tx.interactionCode;
      if (!interactionCode) {
        throw new Error("SUCCESS but no tokens/interactionCode");
      }

      const tokenRes = await oktaAuth.token.exchangeCodeForTokens({
        interactionCode,
      });

      accessToken = tokenRes.tokens?.accessToken?.accessToken;
      idToken = tokenRes.tokens?.idToken?.idToken;
      refreshToken = tokenRes.tokens?.refreshToken?.refreshToken;
    }

    if (!accessToken) {
      throw new Error("No access token received");
    }

    return {
      transaction: tx,
      accessToken,
      idToken,
      refreshToken,
      expiresIn: tokenExpiresIn,
    };
  } catch (err) {
    if (err instanceof AuthApiError) {
      const key = extractAuthApiErrorI18nKey(err);
      throw new Error(key ?? "Unknown authentication error");
    }
    throw err;
  }
};
